/**
 * anonLinkPrompt.js
 *
 * Tracks an anonymous user's request count and fires a custom event when
 * they hit the prompt threshold. The <AnonLinkPrompt /> component listens
 * for that event and renders the modal.
 *
 *   recordRequestAndMaybePrompt(uid, isAnonymous)
 *     Call after every successful song request. Increments a per-uid
 *     counter in localStorage; when count reaches THRESHOLD (and the
 *     dismissal snooze isn't active), dispatches a CustomEvent so the
 *     mounted prompt can show itself.
 *
 *   markPromptDismissed()
 *     Sets a 24h localStorage flag to suppress re-prompts after
 *     "Maybe later".
 *
 *   markPromptResolved(uid)
 *     Clears all per-uid prompt state. Call after a successful link so
 *     the same uid (which is now non-anonymous) never re-prompts.
 *
 * Storage is intentionally device-local — once an anonymous account is
 * linked to Google, isAnonymous flips to false and the prompt guard
 * elsewhere (in <AnonLinkPrompt /> and the request flow) suppresses
 * permanently regardless of stored counters.
 */

// Number of successful song requests an anonymous guest can make before
// they're prompted to link their Google account. Set to 3 so the nag lands
// early — after a little engagement but before they've made many requests.
const THRESHOLD = 3;
const SNOOZE_MS = 24 * 60 * 60 * 1000; // 24 hours
const COUNT_KEY_PREFIX = 'muuzbox_anon_req_count_';
const SNOOZE_KEY = 'muuzbox_anon_link_snooze_until';
const SHOWN_KEY_PREFIX = 'muuzbox_anon_link_shown_';
export const ANON_LINK_PROMPT_EVENT = 'muuzbox:show-anon-link-prompt';

function _getCount(uid) {
  try {
    const v = localStorage.getItem(COUNT_KEY_PREFIX + uid);
    return v ? parseInt(v, 10) || 0 : 0;
  } catch (_) { return 0; }
}

function _setCount(uid, n) {
  try { localStorage.setItem(COUNT_KEY_PREFIX + uid, String(n)); } catch (_) {}
}

function _isSnoozed() {
  try {
    const v = localStorage.getItem(SNOOZE_KEY);
    if (!v) return false;
    const until = parseInt(v, 10) || 0;
    return until > Date.now();
  } catch (_) { return false; }
}

function _alreadyShownForUid(uid) {
  try { return !!localStorage.getItem(SHOWN_KEY_PREFIX + uid); } catch (_) { return false; }
}

function _markShown(uid) {
  try { localStorage.setItem(SHOWN_KEY_PREFIX + uid, String(Date.now())); } catch (_) {}
}

/**
 * Call this after every successful djchats addDoc. Increments the count
 * for the current uid and dispatches the prompt event if the threshold
 * is hit, no snooze is active, and we haven't already shown the prompt
 * for this uid before.
 *
 * `isAnonymous` is the caller's local read of `auth.currentUser.isAnonymous`.
 * When it's `true`, we short-circuit — no Firestore round-trip needed.
 * When it's `false`, we need to double-check via Firestore because the
 * user may have arrived via `signInWithCustomToken` from a bridged
 * parent origin (which flips `isAnonymous` to `false` even though they
 * are logically still a guest). See services/authBridge.js for the
 * full explanation.
 */
export function recordRequestAndMaybePrompt(uid, isAnonymous) {
  if (!uid) return;
  if (isAnonymous) {
    _recordAndMaybeFire(uid);
    return;
  }
  // Custom-token session — verify with Firestore before deciding.
  _checkFirestoreAndMaybeFire(uid).catch((e) => {
    console.warn('[anonLinkPrompt] Firestore anonymousSince check failed:', e?.message);
  });
}

function _recordAndMaybeFire(uid) {
  const n = _getCount(uid) + 1;
  _setCount(uid, n);
  if (n < THRESHOLD) return;
  if (_isSnoozed()) return;
  if (_alreadyShownForUid(uid)) return;
  _markShown(uid);
  try {
    window.dispatchEvent(new CustomEvent(ANON_LINK_PROMPT_EVENT, {
      detail: { uid, requestCount: n },
    }));
  } catch (_) {}
}

// Lazy-imported to avoid pulling Firestore into this service's import
// graph until we actually need it. Only fires on the custom-token path.
async function _checkFirestoreAndMaybeFire(uid) {
  const [{ doc, getDoc }, { db }] = await Promise.all([
    import('firebase/firestore'),
    import('../firebase-config'),
  ]);
  const snap = await getDoc(doc(db, 'users', uid));
  if (!snap.exists()) return;
  const anonymousSince = snap.data()?.anonymousSince;
  if (!anonymousSince) return; // user has properly linked → don't nag
  _recordAndMaybeFire(uid);
}

export function markPromptDismissed() {
  try {
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
  } catch (_) {}
}

export function markPromptResolved(uid) {
  try {
    localStorage.removeItem(COUNT_KEY_PREFIX + uid);
    localStorage.removeItem(SHOWN_KEY_PREFIX + uid);
    localStorage.removeItem(SNOOZE_KEY);
  } catch (_) {}
}
